import { FormEvent, ChangeEvent, Fragment, useEffect, useState } from 'react';

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@material-ui/core';
import { gql, useLazyQuery } from '@apollo/client';
import { Progress } from '@backstage/core-components';
import {
  DevHubButton,
  DevHubModal,
  DevHubTypography,
  DevHubBox,
  DevHubGrid,
  DevHubCard,
  DevHubTextField,
  DevHubCheckbox,
  DevHubFormControl,
  DevHubFormControlLabel,
  DevHubFormGroup,
  DevHubDivider,
  DevHubPaper,
  DevHubChip,
} from '@devxdiscover/devhub-ui';

const GET_APP_ROLES = gql`
  query CheckApplicationRolesByLogin(
    $login: String!
    $filter: ResourceCatalogUserApplicationFilter
  ) {
    identity {
      user(login: $login) {
        resources {
          applications(filter: $filter) {
            name
            roles {
              displayName
            }
          }
        }
      }
    }
  }
`;

/**
 * A component that allows a user to enter a login and see their roles on all applications.
 * @constructor
 */
const UserRolesChecker = () => {
  // const classes = useStyles();
  const [appRoles, setAppRoles] = useState<
    { appName: string; roles: string[] }[]
  >([]);

  const [login, setLogin] = useState<string | undefined>(undefined);
  const [formData, setFormData] = useState({ name: '' });
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const checkboxes = [
    { value: 'admin', label: 'Admin' },
    { value: 'assistant_owner', label: 'Assistant Owner' },
    { value: 'approver', label: 'ITRC Approver' },
    { value: 'dbaas_prod', label: 'DBaaS Prod' },
    { value: 'dbaas_nonprod', label: 'DBaaS Nonprod' },
    { value: 'devops_remediation', label: 'DevOps Remediation' },
    { value: 'global_audit_view', label: 'Global Audit View' },
    { value: 'owner', label: 'Owner' },
    { value: 'spoc', label: 'SPOC' },
    { value: 'stakeholder', label: 'Stakeholder' },
  ];
  const [fetchAppRoles, { data, loading }] = useLazyQuery(GET_APP_ROLES, {
    errorPolicy: 'all',
    variables: {
      login: login,
      filter: {
        havingRoles: selectedRoles,
      },
    },
  });

  const clearAll = () => {
    setSelectedRoles([]);
    setAppRoles([]);
    setLogin(undefined);
    setFormData({ name: '' });
  };

  const handleOpen = () => {
    setModalIsOpen(true);
  };

  const handleClose = () => {
    setModalIsOpen(false);
    clearAll();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLogin(formData.name);
    fetchAppRoles();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
  };

  const handleRoleChange = (role: string) => {
    if (selectedRoles.includes(role)) {
      setSelectedRoles(
        selectedRoles.filter(selectedRole => selectedRole !== role),
      );
    } else {
      setSelectedRoles([...selectedRoles, role]);
    }
  };

  const selectAll = () => {
    setSelectedRoles(checkboxes.map(({ value }) => value));
  };

  useEffect(() => {
    const applications =
      data?.identity?.user?.resources?.applicationsPaged?.applications ??
      data?.identity?.user?.resources?.applications ??
      null;
    if (data && applications) {
      const newAppRoles = applications.map(
        (app: { name: any; roles: { displayName: any }[] }) => ({
          appName: app.name,
          roles: app.roles.map(role => role.displayName),
        }),
      );
      newAppRoles.sort((a: { appName: string }, b: { appName: any }) =>
        a.appName.localeCompare(b.appName),
      );
      setAppRoles(newAppRoles);
    }
  }, [data, selectedRoles]);

  return (
    <Fragment>
      <DevHubCard
        density="compact"
        title="Role Checker"
        actionItem={
          <DevHubGrid container margin="xsmall">
            <DevHubButton
              buttonColor="primary"
              variant="outlined"
              onClick={handleOpen}
            >
              Open
            </DevHubButton>
          </DevHubGrid>
        }
        subTitle="Search for User Roles by NT"
      />
      <DevHubModal open={modalIsOpen} onClose={handleClose}>
        <Fragment>
          <DevHubTypography variant="h3">Role Checker</DevHubTypography>
          <DevHubTypography variant="body2" fontColor="medGray">
            Search for a user's application roles by NT ID.
          </DevHubTypography>

          {/* NT ID Search */}
          <DevHubBox mt={3}>
            <DevHubTypography variant="subtitle2" weight="bold">
              NT ID
            </DevHubTypography>
            <DevHubBox
              display="flex"
              alignItems="center"
              mt={1}
              style={{ gap: '8px' }}
            >
              <DevHubTextField
                label="Enter NT ID"
                type="text"
                name="name"
                variant="outlined"
                value={formData.name}
                onChange={handleChange}
                noMargin
              />
              <DevHubButton
                type="submit"
                variant="contained"
                buttonColor="primary"
                height="large"
                onClick={(e: any) => handleSubmit(e)}
              >
                Search
              </DevHubButton>
              <DevHubButton
                variant="outlined"
                height="large"
                onClick={clearAll}
              >
                Reset
              </DevHubButton>
            </DevHubBox>
          </DevHubBox>

          <DevHubBox mt={3} mb={1}>
            <DevHubDivider />
          </DevHubBox>

          {/* Role Filters */}
          <DevHubBox mt={2}>
            <DevHubBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <DevHubTypography variant="subtitle2" weight="bold">
                Filter by Role
              </DevHubTypography>
              <DevHubBox display="flex" style={{ gap: '8px' }}>
                <DevHubButton variant="outlined" onClick={selectAll}>
                  Select All
                </DevHubButton>
                <DevHubButton
                  variant="outlined"
                  onClick={() => setSelectedRoles([])}
                >
                  Clear
                </DevHubButton>
              </DevHubBox>
            </DevHubBox>
            <DevHubFormControl>
              <DevHubGrid container>
                {checkboxes.map(({ value, label }) => (
                  <DevHubGrid item key={value} xs={6} sm={4}>
                    <DevHubFormGroup>
                      <DevHubFormControlLabel
                        control={
                          <DevHubCheckbox
                            checked={selectedRoles.includes(value)}
                            onChange={() => handleRoleChange(value)}
                            value={value}
                            color="primary"
                          />
                        }
                        label={
                          <DevHubTypography variant="body2">
                            {label}
                          </DevHubTypography>
                        }
                      />
                    </DevHubFormGroup>
                  </DevHubGrid>
                ))}
              </DevHubGrid>
            </DevHubFormControl>
          </DevHubBox>

          {/* Loading */}
          {loading && (
            <DevHubBox mt={2}>
              <Progress />
            </DevHubBox>
          )}

          {/* No Results */}
          {!loading && login && appRoles.length === 0 && formData.name && (
            <DevHubBox mt={3}>
              <DevHubDivider />
              <DevHubBox mt={2}>
                <DevHubTypography variant="body1">
                  No roles found for <strong>{login}</strong>.
                </DevHubTypography>
              </DevHubBox>
            </DevHubBox>
          )}

          {/* Results Table */}
          {!loading && appRoles.length > 0 && (
            <DevHubBox mt={3}>
              <DevHubDivider />
              <DevHubBox
                mt={2}
                mb={1}
                display="flex"
                alignItems="center"
                style={{ gap: '8px' }}
              >
                <DevHubTypography variant="subtitle2" weight="bold">
                  Results for
                </DevHubTypography>
                <DevHubChip
                  label={login}
                  chipColor="primary"
                  chipSize="small"
                  noMargins
                />
                <DevHubChip
                  label={`${appRoles.length} app${appRoles.length !== 1 ? 's' : ''}`}
                  chipColor="secondary"
                  chipSize="small"
                  noMargins
                />
              </DevHubBox>
              <TableContainer
                component={DevHubPaper}
                slim
                style={{ maxHeight: 400 }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                        <strong>Application</strong>
                      </TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                        <strong>Roles</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {appRoles.map(({ appName, roles }, index) => (
                      <TableRow key={index}>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          <DevHubTypography variant="body2">
                            {appName}
                          </DevHubTypography>
                        </TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          <DevHubBox
                            display="flex"
                            flexWrap="wrap"
                            style={{ gap: '4px' }}
                          >
                            {roles.map(role => (
                              <DevHubChip
                                key={role}
                                label={role}
                                chipColor="primary"
                                chipSize="small"
                                noMargins
                              />
                            ))}
                          </DevHubBox>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DevHubBox>
          )}
        </Fragment>
      </DevHubModal>
    </Fragment>
  );
};

export default UserRolesChecker;
